import { Button } from '@main/complykit';
import { Callout } from '@main/complykit/components/callout';
import { Stack } from '@main/complykit/components/stack';
import { isConfigurableSchema } from '@main/shared/utils';
import { Namespace, TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod/v4';

import { MarkdownText } from '../markdown-text';
import {
  hasDirtyValues,
  makeIntegrationZodResolver,
  nullToUndefined,
  SchemaForm,
} from '../schema-form';
import {
  ConnectorConfigDraftValues,
  ConnectorConfigSubmit,
  createConnectorConfigFormModel,
  EncryptedConfigFieldState,
} from './connector-config-model';

export type ConnectorConfigSubmitResult =
  | { status: 'committed' }
  | { status: 'notCommitted'; reason: 'cancelled' | 'failed' };

export type ConnectorConfigFormProps = {
  t: TFunction<Namespace>;
  tKey: string;
  configSchema: z.ZodObject;
  encryptedConfigSchema: z.ZodObject;
  defaultConfig?: Record<string, unknown>;
  encryptedFields: EncryptedConfigFieldState[];
  submitLabel: string;
  requireDirtyToSubmit: boolean;
  renderActionsWhenEmpty?: boolean;
  onCancel: () => void;
  onSubmit: (
    value: ConnectorConfigSubmit,
  ) => ConnectorConfigSubmitResult | Promise<ConnectorConfigSubmitResult>;
};

export const ConnectorConfigForm = ({
  t,
  tKey,
  configSchema,
  encryptedConfigSchema,
  defaultConfig,
  encryptedFields,
  submitLabel,
  requireDirtyToSubmit,
  renderActionsWhenEmpty = false,
  onCancel,
  onSubmit,
}: ConnectorConfigFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clearedEncryptedPaths, setClearedEncryptedPaths] = useState(() => new Set<string>());

  const hasPlainSchema = isConfigurableSchema(configSchema);
  const hasEncryptedSchema = isConfigurableSchema(encryptedConfigSchema);
  const hasConfigFields = hasPlainSchema || hasEncryptedSchema;
  const instructionsTitle = t(`${tKey}.instructionsTitle`, { defaultValue: '' });
  const instructions = t(`${tKey}.instructions`, { defaultValue: '' });
  const model = useMemo(
    () =>
      createConnectorConfigFormModel({
        configSchema,
        encryptedConfigSchema,
        defaultConfig,
        encryptedFields,
        clearedEncryptedPaths,
        t,
        tKey,
      }),
    [
      clearedEncryptedPaths,
      configSchema,
      defaultConfig,
      encryptedConfigSchema,
      encryptedFields,
      t,
      tKey,
    ],
  );
  const form = useForm<ConnectorConfigDraftValues>({
    defaultValues: model.defaultValues,
    resolver: makeIntegrationZodResolver(model.inputSchema, t),
  });

  const hasDirtyFields =
    hasDirtyValues(form.formState.dirtyFields.config) ||
    hasDirtyValues(form.formState.dirtyFields.encryptedConfig);
  const isSubmitDisabled =
    requireDirtyToSubmit && !hasDirtyFields && clearedEncryptedPaths.size === 0;

  const handleSubmit = form.handleSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(
        model.serialize({
          values: nullToUndefined(values),
          dirtyFields: form.formState.dirtyFields,
        }),
      );
      if (result.status === 'committed') {
        form.reset(model.defaultValues);
        setClearedEncryptedPaths(new Set());
      }
    } catch {
      // Submit failures are reported by the persistence owner. Keep the draft intact.
    } finally {
      setIsSubmitting(false);
    }
  });

  if (!hasConfigFields && !renderActionsWhenEmpty) {
    return null;
  }

  return (
    <Stack gap={4}>
      {hasConfigFields && instructions && (
        <Callout status="neutral" title={instructionsTitle || undefined}>
          <MarkdownText text={instructions} />
        </Callout>
      )}
      {hasConfigFields && (
        <SchemaForm
          model={model}
          form={form}
          isDisabled={isSubmitting}
          slotRegistry={{
            'connector.encryptedFieldFooter': ({ node, form, isDisabled }) => {
              if (!node.meta || node.meta.part !== 'encryptedConfig') {
                return null;
              }

              const fieldId = node.path.join('.');

              return (
                <ClearEncryptedFieldButton
                  key={fieldId}
                  isCleared={node.meta.clearState === 'cleared'}
                  isDisabled={isDisabled}
                  t={t}
                  onClick={() => {
                    if (
                      node.meta?.part === 'encryptedConfig' &&
                      node.meta.clearState !== 'cleared'
                    ) {
                      form.resetField(node.name);
                    }
                    setClearedEncryptedPaths((current) => {
                      const next = new Set(current);
                      if (next.has(fieldId)) {
                        next.delete(fieldId);
                      } else {
                        next.add(fieldId);
                      }
                      return next;
                    });
                  }}
                />
              );
            },
          }}
        />
      )}
      <Stack direction="row" gap={2} justify="end">
        <Button variant="soft" isDisabled={isSubmitting} onPress={onCancel}>
          {t('configureForm.cancelBtn')}
        </Button>
        <Button
          isPending={isSubmitting}
          isDisabled={isSubmitDisabled}
          onPress={() => void handleSubmit()}
        >
          {submitLabel}
        </Button>
      </Stack>
    </Stack>
  );
};

type ClearEncryptedFieldButtonProps = {
  isCleared: boolean;
  isDisabled: boolean;
  onClick: () => void;
  t: TFunction<Namespace>;
};

const ClearEncryptedFieldButton = ({
  isCleared,
  isDisabled,
  onClick,
  t,
}: ClearEncryptedFieldButtonProps) => {
  return (
    <Button
      variant="ghost"
      size="sm"
      tone={isCleared ? undefined : 'critical'}
      isDisabled={isDisabled}
      onPress={onClick}
    >
      {t(isCleared ? 'configureForm.undoClearSavedValueBtn' : 'configureForm.clearSavedValueBtn')}
    </Button>
  );
};
